<script setup lang="ts">
import { ref, computed } from 'vue'
import { MAX_FILE_SIZE, EXTENSOES_ACEITAS } from '@/config/validation'

interface Props {
  accept?: string
  maxSize?: number
  disabled?: boolean
  currentFile?: File | null
}

interface Emits {
  (e: 'file-selected', file: File): void
  (e: 'file-removed'): void
  (e: 'error', message: string): void
}

const props = withDefaults(defineProps<Props>(), {
  accept: EXTENSOES_ACEITAS.join(','),
  maxSize: MAX_FILE_SIZE,
  disabled: false,
  currentFile: null
})

const emit = defineEmits<Emits>()

const isDragging = ref(false)
const inputRef = ref<HTMLInputElement | null>(null)

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const maxSizeFormatted = computed(() => formatFileSize(props.maxSize))

const validateFile = (file: File): string | null => {
  const temExtensao = file.name.includes('.')
  const extension = file.name.split('.').pop()?.toLowerCase()
  const allowedExtensions = props.accept
    .split(',')
    .map(ext => ext.trim().replace('.', '').toLowerCase())

  // Rejeita arquivos sem extensão ou fora do formato aceito (.xlsx)
  if (!temExtensao || !extension || !allowedExtensions.includes(extension)) {
    return 'Formato inválido: o arquivo precisa ser uma planilha Excel no formato .xlsx. Baixe e preencha um dos modelos (templates) disponíveis nesta tela e reenvie o arquivo salvo em .xlsx.'
  }

  if (file.size > props.maxSize) {
    return `Arquivo muito grande. O tamanho máximo é ${maxSizeFormatted.value}.`
  }

  return null
}

const handleFile = (file: File) => {
  const error = validateFile(file)
  if (error) {
    emit('error', error)
    return
  }
  emit('file-selected', file)
}

const onDrop = (e: DragEvent) => {
  isDragging.value = false
  if (props.disabled) return

  const files = e.dataTransfer?.files
  if (files && files.length > 0) {
    handleFile(files[0])
  }
}

const onDragOver = (e: DragEvent) => {
  e.preventDefault()
  if (!props.disabled) {
    isDragging.value = true
  }
}

const onDragLeave = () => {
  isDragging.value = false
}

const onClick = () => {
  if (!props.disabled && inputRef.value) {
    inputRef.value.click()
  }
}

const onFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const files = target.files
  if (files && files.length > 0) {
    handleFile(files[0])
  }
  target.value = ''
}

const removeFile = () => {
  emit('file-removed')
}
</script>

<template>
  <div>
    <!-- Área de Drop quando não tem arquivo -->
    <div
      v-if="!currentFile"
      class="drop-zone"
      :class="{
        'drop-zone--active': isDragging,
        'drop-zone--disabled': disabled
      }"
      @drop.prevent="onDrop"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @click="onClick"
    >
      <input
        ref="inputRef"
        type="file"
        :accept="accept"
        :disabled="disabled"
        class="d-none"
        @change="onFileChange"
      />

      <!-- Ícone de arquivo com badges -->
      <div class="file-icon-container">
        <div class="file-icon-badge-plus">
          <v-icon size="16" color="white">mdi-plus</v-icon>
        </div>
        <div class="file-icon">
          <v-icon size="48" color="#3B82F6">mdi-file-document-outline</v-icon>
        </div>
        <div class="file-icon-badge-xlsx">XLSX</div>
      </div>

      <p class="drop-zone__title">
        Arraste o arquivo Excel aqui
      </p>
      <p class="drop-zone__subtitle">
        ou clique para selecionar do seu computador
      </p>

      <div class="drop-zone__info">
        <span>
          <v-icon size="14" class="mr-1">mdi-check-circle</v-icon>
          Formato: .xlsx
        </span>
        <span>
          <v-icon size="14" class="mr-1">mdi-file</v-icon>
          Max: 100 MB
        </span>
      </div>
    </div>

    <!-- Preview do arquivo selecionado -->
    <div v-else class="file-preview">
      <div class="file-preview__icon">
        <v-icon size="40" color="#22C55E">mdi-file-excel</v-icon>
      </div>
      <div class="file-preview__info">
        <div class="file-preview__name">{{ currentFile.name }}</div>
        <div class="file-preview__size">{{ formatFileSize(currentFile.size) }}</div>
      </div>
      <v-btn
        icon
        variant="text"
        color="error"
        size="small"
        :disabled="disabled"
        @click.stop="removeFile"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style scoped lang="scss">
.drop-zone {
  border: 2px dashed #CBD5E1;
  border-radius: 12px;
  padding: 32px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: #FFFFFF;

  &:hover:not(.drop-zone--disabled) {
    border-color: #3B82F6;
    background-color: #F8FAFC;
  }

  &--active {
    border-color: #3B82F6;
    background-color: #EFF6FF;
    border-style: solid;
  }

  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &__title {
    font-size: 16px;
    font-weight: 500;
    color: #1E293B;
    margin: 0 0 4px 0;
  }

  &__subtitle {
    font-size: 14px;
    color: #64748B;
    margin: 0 0 16px 0;
  }

  &__info {
    display: flex;
    justify-content: center;
    gap: 24px;
    font-size: 13px;
    color: #64748B;

    span {
      display: flex;
      align-items: center;
    }
  }
}

.file-icon-container {
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
}

.file-icon {
  background: #EFF6FF;
  border-radius: 12px;
  padding: 16px;
  display: inline-flex;
}

.file-icon-badge-plus {
  position: absolute;
  top: -8px;
  right: -8px;
  background: #F59E0B;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
}

.file-icon-badge-xlsx {
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  background: #F59E0B;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

.file-preview {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #F0FDF4;
  border: 1px solid #BBF7D0;
  border-radius: 12px;

  &__icon {
    margin-right: 12px;
  }

  &__info {
    flex-grow: 1;
  }

  &__name {
    font-size: 14px;
    font-weight: 500;
    color: #1E293B;
    word-break: break-all;
  }

  &__size {
    font-size: 12px;
    color: #64748B;
  }
}
</style>
